import {
  effect,
  inject,
  Injectable,
  OnDestroy,
  untracked,
} from '@angular/core';
import { Packet } from '@escrowtab/packets-api-client';
import { ProfileDto } from '@escrowtab/v2profile-api-client';
import { IpService } from '@et/api';
import {
  AuditAction,
  EtJournalEntry,
  EtJournalESeal,
  EtjournalPacketEntry,
  EtJournalSigner,
} from '@et/typings';
import { Store } from '@ngxs/store';
import { Observable, throwError } from 'rxjs';
import {
  ClearEjournalSession,
  ResetEjournalSession,
  SetEjournal,
  SetEjournalLoading,
  SetEntries,
  SetIp,
  SetJournalPayload,
  SetNotarizedAuditActions,
  SetProfile,
  SetSelectedPacket,
  SetSigners,
  UpdateEntries,
  UpdateSigners,
} from '../state/ejournal-session.actions';
import { EjournalSessionSelectors } from '../state/ejournal-session.selectors';
import { EjournalEntriesService } from './ejournal-entries.service';
import { EjournalFinalizeService } from './ejournal-finalize.service';
import { EjournalLoadService } from './ejournal-load.service';
import { EjournalPacketPdfService } from './ejournal-packet-pdf.service';
import { EjournalSealsService } from './ejournal-seals.service';
import { EjournalSessionCancelService } from './ejournal-session-cancel.service';
import { EjournalSignersService } from './ejournal-signers.service';

/**
 * Facade for the eJournal session: exposes NGXS signals/setters and delegates
 * load / signer / entry / seal / finalize work to session-scoped helpers.
 */
@Injectable()
export class EjournalService implements OnDestroy {
  private readonly sessionCancel = inject(EjournalSessionCancelService);
  private readonly loadService = inject(EjournalLoadService);
  private readonly signersService = inject(EjournalSignersService);
  private readonly entriesService = inject(EjournalEntriesService);
  private readonly sealsService = inject(EjournalSealsService);
  private readonly finalizeService = inject(EjournalFinalizeService);
  private readonly packetPdfService = inject(EjournalPacketPdfService);
  private readonly ipService = inject(IpService);
  private readonly store = inject(Store);

  /** True when packet PDF was supplied by the caller (e.g. dashboard modal). */
  private packetFileFromCaller = false;
  /** Caller-supplied packet PDF; re-applied after packet reset so the effect does not wipe it. */
  private callerPacketFile: File | null = null;

  profile = this.store.selectSignal(EjournalSessionSelectors.getProfile);
  selectedPacket = this.store.selectSignal(
    EjournalSessionSelectors.getSelectedPacket,
  );
  eJournal = this.store.selectSignal(EjournalSessionSelectors.getEjournal);
  signers = this.store.selectSignal(EjournalSessionSelectors.getSigners);
  entries = this.store.selectSignal(EjournalSessionSelectors.getEntries);
  sealEntries = this.store.selectSignal(
    EjournalSessionSelectors.getSealEntries,
  );
  cleanEntries = this.store.selectSignal(
    EjournalSessionSelectors.getCleanEntries,
  );
  notarizedAuditActions = this.store.selectSignal(
    EjournalSessionSelectors.getNotarizedAuditActions,
  );
  notarizedAuditLoaded = this.store.selectSignal(
    EjournalSessionSelectors.getNotarizedAuditLoaded,
  );
  pageNamesByNumber = this.store.selectSignal(
    EjournalSessionSelectors.getPageNamesByNumber,
  );
  eJournalLoading = this.store.selectSignal(
    EjournalSessionSelectors.getEjournalLoading,
  );
  signersLoading = this.store.selectSignal(
    EjournalSessionSelectors.getSignersLoading,
  );
  entriesLoading = this.store.selectSignal(
    EjournalSessionSelectors.getEntriesLoading,
  );
  eJournalError = this.store.selectSignal(
    EjournalSessionSelectors.getEjournalError,
  );
  signersError = this.store.selectSignal(
    EjournalSessionSelectors.getSignersError,
  );
  entriesError = this.store.selectSignal(
    EjournalSessionSelectors.getEntriesError,
  );
  ip = this.store.selectSignal(EjournalSessionSelectors.getIp);

  /**
   * Subscribes to the selected packet signal: resets state and loads the first eJournal for the packet when set.
   */
  constructor() {
    effect(() => {
      const selectedPacket = this.selectedPacket();
      this.reset();
      if (selectedPacket) {
        untracked(() => {
          this.loadService.loadEjournalsByPacketId(selectedPacket.packetId!);
          this.loadService.loadAuditLogByPacketId(selectedPacket.packetId!);
          // Defer so callers (e.g. dashboard modal) can set page names / PDF before we fetch.
          queueMicrotask(() => {
            if (this.loadService.hasCallerPageNames()) {
              this.loadService.reapplyCallerPageNames();
            } else {
              this.loadService.loadPageNamesByPacketId(
                selectedPacket.packetId!,
              );
            }
            if (
              this.packetFileFromCaller &&
              this.callerPacketFile &&
              selectedPacket.packetId
            ) {
              this.packetPdfService.setPacketFile(
                this.callerPacketFile,
                selectedPacket.packetId,
              );
            }
          });
        });
      }
    });

    this.ipService.getMyIP().subscribe((ip) => this.setIp(ip));
  }

  setProfile(profile: ProfileDto | undefined) {
    this.store.dispatch(new SetProfile(profile));
  }

  setSelectedPacket(selectedPacket: Packet | null) {
    this.loadService.resetPacketScopedState();
    this.packetFileFromCaller = false;
    this.callerPacketFile = null;
    this.store.dispatch(new SetSelectedPacket(selectedPacket));
  }

  /**
   * Sets page number → document name map from the caller (e.g. signing-room markup).
   * Skips / ignores the markup API fetch for the current packet selection.
   */
  setPageNamesByNumber(pageNamesByNumber: Record<number, string>) {
    this.loadService.setPageNamesByNumber(pageNamesByNumber);
  }

  setEJournal(eJournal: EtjournalPacketEntry | null) {
    this.store.dispatch(new SetEjournal(eJournal));
  }

  setSigners(signers: EtJournalSigner[]) {
    this.store.dispatch(new SetSigners(signers));
  }

  setEntries(entries: EtJournalEntry[]) {
    this.store.dispatch(new SetEntries(entries));
  }

  updateSigners(updater: (signers: EtJournalSigner[]) => EtJournalSigner[]) {
    this.store.dispatch(new UpdateSigners(updater));
  }

  updateEntries(updater: (entries: EtJournalEntry[]) => EtJournalEntry[]) {
    this.store.dispatch(new UpdateEntries(updater));
  }

  setEJournalLoading(eJournalLoading: boolean) {
    this.store.dispatch(new SetEjournalLoading(eJournalLoading));
  }

  setIp(ip: string | null) {
    this.store.dispatch(new SetIp(ip));
  }

  setJournalFromGenerator(
    eJournal: EtjournalPacketEntry,
    signers: EtJournalSigner[],
    entries: EtJournalEntry[],
  ) {
    this.store.dispatch(new SetJournalPayload(eJournal, signers, entries));
  }

  setNotarizedAuditActions(actions: AuditAction[]) {
    this.store.dispatch(new SetNotarizedAuditActions(actions));
  }

  clearSession() {
    this.packetFileFromCaller = false;
    this.callerPacketFile = null;
    this.packetPdfService.clear();
    this.store.dispatch(new ClearEjournalSession());
  }

  /**
   * Stores a caller-supplied packet PDF (e.g. dashboard opened in a modal with an in-memory file).
   * Survives the packet-selection reset via the same microtask re-apply pattern as page names.
   */
  setPacketPdfFile(file: File): void {
    const packetId = this.selectedPacket()?.packetId;
    if (!packetId) {
      return;
    }
    this.packetFileFromCaller = true;
    this.callerPacketFile = file;
    this.packetPdfService.setPacketFile(file, packetId);
  }

  /**
   * Returns the packet PDF for the selected packet (cached external file or fetched signed/unsigned).
   */
  getPacketPdfFile(): Observable<File> {
    const packet = this.selectedPacket();
    const packetId = packet?.packetId;
    if (!packetId) {
      return throwError(() => new Error('No selected packet'));
    }
    const signed = packet.utcDateSigned != null;
    return this.packetPdfService.getPacketFile(packetId, signed);
  }

  /** @see EjournalLoadService.loadEjournalsByPacketId */
  loadEjournalsByPacketId(packetId: string) {
    this.loadService.loadEjournalsByPacketId(packetId);
  }

  /** @see EjournalLoadService.loadAuditLogByPacketId */
  loadAuditLogByPacketId(packetId: string) {
    this.loadService.loadAuditLogByPacketId(packetId);
  }

  /** @see EjournalLoadService.loadPageNamesByPacketId */
  loadPageNamesByPacketId(packetId: string) {
    this.loadService.loadPageNamesByPacketId(packetId);
  }

  /** @see EjournalLoadService.loadNotarizedAuditActionsFromState */
  loadNotarizedAuditActionsFromState(actions: AuditAction[]) {
    this.loadService.loadNotarizedAuditActionsFromState(actions);
  }

  /** @see EjournalSignersService.deleteSigner */
  deleteSigner(signerId: string) {
    return this.signersService.deleteSigner(signerId);
  }

  /** @see EjournalSignersService.deleteSignerSignature */
  deleteSignerSignature(signerId: string, signatureId: string) {
    return this.signersService.deleteSignerSignature(signerId, signatureId);
  }

  /** @see EjournalEntriesService.deleteEntry */
  deleteEntry(entry: EtJournalEntry) {
    return this.entriesService.deleteEntry(entry);
  }

  /** @see EjournalSealsService.getSeals */
  getSeals(): EtJournalESeal[] {
    return this.sealsService.getSeals();
  }

  /** @see EjournalSealsService.getCancelActEntries */
  getCancelActEntries(): EtJournalEntry[] {
    return this.sealsService.getCancelActEntries();
  }

  /** @see EjournalSealsService.saveSeal */
  saveSeal(nextSeal: EtJournalESeal) {
    return this.sealsService.saveSeal(nextSeal);
  }

  /** @see EjournalSealsService.deleteSeal */
  deleteSeal(seal: EtJournalESeal) {
    return this.sealsService.deleteSeal(seal);
  }

  /** @see EjournalFinalizeService.finalizeEJournal */
  finalizeEJournal() {
    return this.finalizeService.finalizeEJournal();
  }

  /**
   * @deprecated Prefer {@link EjournalEntriesService.sortEntries}; kept for call-site compatibility.
   */
  static sortEntries(entries: EtJournalEntry[]) {
    return EjournalEntriesService.sortEntries(entries);
  }

  /**
   * Clears journal-related state and cancels in-flight loads so a new packet selection starts clean.
   * Does not clear the signing-state overlay — that is reset in `setSelectedPacket` so callers can
   * set overlay after selecting a packet before this effect runs.
   */
  private reset() {
    this.packetPdfService.clear();
    this.store.dispatch(new ResetEjournalSession());
    this.sessionCancel.cancel();
  }

  /**
   * Completes the cancel subject so subscriptions tied to this service stop after destroy.
   */
  ngOnDestroy(): void {
    this.sessionCancel.complete();
    this.clearSession();
  }
}
